import React, { ReactElement, useEffect, useState } from 'react';
import classNames from 'classnames';

import { deleteCompleted, postCompleted } from '@tsClient';
import { mixpanelTrack } from '@providers/Mixpanel';
import useNoodleApi from '@hooks/useNoodleApi';
import Buttons from '@components/Buttons';
import CheckCircle from '@components/Icons/CheckCircle';
import { useUserProfile } from '@providers/Auth';

import s from './MarkCompleted.module.scss';

type Props = {
  id: string;
  isDisabled?: boolean;
};

type CompletedType = Record<string, string>;

const MarkCompleted = ({ id, isDisabled }: Props): ReactElement => {
  const profile = useUserProfile();
  const { fetchingState: createFetchingState, getData: postCompletedFn } = useNoodleApi(postCompleted);
  const { fetchingState: deleteFetchingState, getData: deleteCompletedFn } = useNoodleApi(deleteCompleted);

  const [completedState, setCompletedState] = useState<CompletedType>(profile?.completed || {});

  const isCompleted = Object.keys(completedState).includes(id);

  useEffect(() => {
    if (profile?.completed) {
      setCompletedState(profile.completed);
    }
  }, [profile]);

  const handleCompleted = async (): Promise<void> => {
    const newCompleted = { ...completedState };
    if (isCompleted) {
      await deleteCompletedFn({ entityId: id });
      delete newCompleted[id];
    } else {
      await postCompletedFn({ entityId: id });
      mixpanelTrack('Completed handbook', {
        productId: id,
      });
      newCompleted[id] = new Date().toString();
    }
    setCompletedState(newCompleted);
  };

  const isFetching = !profile || createFetchingState.isFetching || deleteFetchingState.isFetching;

  return (
    <Buttons
      isThird
      isLarge
      isShimmering={isFetching}
      disabled={isDisabled}
      onClick={handleCompleted}
      title={`Mark as ${(isCompleted && 'not ') || ''}completed`}
      className={classNames(s['mark-completed'], { [s['mark-completed--checked']]: isCompleted })}
      icon={
        <CheckCircle weight="fill" color={isCompleted ? 'var(--color-gray-0)' : 'var(--color-primary)'} />
      }
    ></Buttons>
  );
};

export default MarkCompleted;
